import { useDispatch } from 'react-redux';
import { useQueryParams } from '@/hooks/use-query-params';

/**
 * Hook for processing and applying selected filters with configurable actions
 */

const ARRAY_CONDITION_OPERATORS = ['is', 'is_not', 'and'];

export const useApplyFilterHandler = ({
  applyFilter,
  customFilters = [],
  asyncFields = [],
  locationFieldKey = 'location',

  // use this for saving filters in separate object in redux - optional
  setFiltersData,
  filterConfig = {},
}) => {
  const dispatch = useDispatch();
  const queryParams = useQueryParams();
  const stageID = queryParams.get('stageId') || null;
  const handleApplyFilter = (filters) => {
    if (!filters || filters.length === 0) {
      console.warn('No filters provided');
      return;
    }

    // Save raw filters
    if (setFiltersData) {
      dispatch(setFiltersData(filters));
    } else {
      dispatch(applyFilter({ feConfig: filters, isNewFilter: true }));
    }

    // Process and format filters
    const formattedFilters = filters.reduce((acc, filter) => {
      const { filter_by, filter_condition, filter_by_values } = filter;
      const filterByValue = filter_by.value;
      const filterConditionValue = filter_condition.value;
      const customFieldId = filter_by.id; // Get custom field ID from filter_by

      const customField = customFilters?.find(
        (cField) => cField.id === customFieldId || cField.id === filterByValue
      );

      const formattedValues = filter_by_values.map((value) =>
        value === 'true' ? true : value === 'false' ? false : value
      );

      const customFormattedValues = filter_by_values.map((value) =>
        value === 'true' ? 'Y' : value === 'false' ? 'N' : value
      );

      if (customField) {
        // Handle custom fields
        return processCustomField({
          acc,
          customField,
          customFormattedValues,
          condition: filterConditionValue,
        });
      } else if (formattedValues.length) {
        if (filterByValue === locationFieldKey) {
          // Handle location fields
          return processLocationField({
            acc,
            values: formattedValues,
            condition: filterConditionValue,
          });
        } else if (filterByValue === 'stage_stuck_days') {
          acc[filterByValue] = {
            stage_id: stageID,
            days: filter_by_values?.[0],
          };
          return acc;
        }

        // handle date range differently
        else if (
          filter?.formConfig?.type === 'filterDateRangePicker' ||
          filter?.formConfig?.type === 'yearRangePicker'
        ) {
          acc[filterByValue] = {
            gte: filter_by_values?.[0]?.from,
            lte: filter_by_values?.[0]?.to,
          };
          return acc;
        } else if (asyncFields.includes(filterByValue)) {
          // Handle async fields
          return processAsyncField({
            acc,
            fieldKey: filterByValue,
            values: formattedValues,
            condition: filterConditionValue,
          });
        } else {
          // Handle standard fields
          return processStandardField({
            acc,
            fieldKey: filterByValue,
            values: formattedValues,
            condition: filterConditionValue,
            filterConfig,
          });
        }
      }
      return acc;
    }, {});

    // Apply formatted filters
    dispatch(applyFilter({ isNewFilter: true, beConfig: formattedFilters }));
  };

  return { handleApplyFilter };
};

const processCustomField = ({
  acc,
  customField,
  customFormattedValues,
  condition,
}) => {
  const filterId = customField.id;
  const isDate = customField?.kind === 'date';
  if (!acc.custom_filter) acc.custom_filter = {};

  if (isDate) {
    const dateValue = new Date(customFormattedValues[0]);
    const epochStart = Math.floor(
      new Date(dateValue.setHours(0, 0, 0, 0)).getTime() / 1000
    );
    const epochEnd = Math.floor(
      new Date(dateValue.setHours(23, 59, 59, 999)).getTime() / 1000
    );

    acc.custom_filter[filterId] =
      condition === 'eq'
        ? { gte: epochStart, lt: epochEnd }
        : { [condition]: Math.floor(dateValue.getTime() / 1000) };
  } else {
    const value =
      !ARRAY_CONDITION_OPERATORS.includes(condition) &&
      customFormattedValues?.length === 1
        ? customFormattedValues?.[0]
        : customFormattedValues;

    acc.custom_filter[filterId] =
      value === 'Y' || value === 'N' ? value : { [condition]: value };
  }

  return acc;
};

const processLocationField = ({ acc, values, condition }) => {
  const locationFilters = values.reduce(
    (locAcc, { places_country_id, places_city_id, places_state_id }) => {
      // Initialize arrays if not already present
      if (places_country_id) {
        locAcc.places_country_id = locAcc.places_country_id || {
          [condition]: [],
        };
        locAcc.places_country_id[condition].push(places_country_id);
      }
      if (places_city_id) {
        locAcc.places_city_id = locAcc.places_city_id || { [condition]: [] };
        locAcc.places_city_id[condition].push(places_city_id);
      }
      if (places_state_id) {
        locAcc.places_state_id = locAcc.places_state_id || { [condition]: [] };
        locAcc.places_state_id[condition].push(places_state_id);
      }
      return locAcc;
    },
    {}
  );

  // Convert arrays with single value to direct values
  Object.keys(locationFilters).forEach((key) => {
    if (
      !ARRAY_CONDITION_OPERATORS.includes(condition) &&
      locationFilters[key][condition].length === 1
    ) {
      locationFilters[key][condition] = locationFilters[key][condition][0];
    }
  });

  return { ...acc, ...locationFilters };
};

const processAsyncField = ({ acc, values, condition, fieldKey }) => {
  acc[fieldKey] = {
    [condition]:
      !ARRAY_CONDITION_OPERATORS.includes(condition) && values.length === 1
        ? values[0].id || values[0].value
        : values.map((v) => v.id || v.value),
  };
  return acc;
};

const processStandardField = ({
  acc,
  values,
  condition,
  fieldKey,
  filterConfig,
}) => {
  // const finalValue = values.length > 1 ? values : values[0]; // old code
  let finalValue =
    !ARRAY_CONDITION_OPERATORS.includes(condition) && values.length === 1
      ? values[0]
      : values;

  if (values.length === 1 && typeof values[0] === 'boolean') {
    finalValue = values[0];
  }

  const isReturnAllValues =
    filterConfig?.[fieldKey]?.setRawValuesIfEnabled ?? false;
  acc[fieldKey] =
    typeof finalValue === 'boolean'
      ? finalValue
      : isReturnAllValues
        ? values
        : { [condition]: finalValue };
  return acc;
};
